// src/screens/LoginScreen.js
import React, { useState, useRef } from 'react';
import {
  ScrollView,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Image,
  StyleSheet,
  ActivityIndicator,
  Linking,
  Platform,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import {
  signInWithEmailAndPassword,
  signInWithCustomToken,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  getAdditionalUserInfo,
  deleteUser,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../../firebaseConfig';
import { preferRedirectForGoogle, withTimeout, googleSignInErrorText } from '../utils/googleAuth';
import { saveOAuthState, takeOAuthState } from '../utils/oauthState';
import { FORGOT_LINK_LABEL, setResetPrefill } from '../utils/passwordReset';
import { isDesktopApp } from '../utils/platform';
import {
  toE164,
  phoneSendErrorText,
  codeSentLine,
  afterPhoneConfirm,
  deleteOrSignOut,
  createPhoneThrottle,
  createRecaptchaSlot,
  loginGuard,
  nextTarget,
  saveOAuthNext,
  takeOAuthNext,
  getSessionStorage,
  desktopAuthLine,
  signOutWithDesktopPush,
  PHONE_REGION_HINT,
  PHONE_SIGN_IN_LABEL,
  PHONE_NUMBER_PLACEHOLDER,
  TEXT_ME_A_CODE_LABEL,
  BACK_TO_EMAIL_LABEL,
  SMS_RATES_LINE,
  PHONE_CODE_PLACEHOLDER,
  PHONE_CONFIRM_LABEL,
  USE_DIFFERENT_NUMBER_LABEL,
  TEXT_ME_A_NEW_CODE_LABEL,
  PHONE_INVALID_TEXT,
  PHONE_CODE_REQUIRED_TEXT,
  PHONE_ERR,
  SIGN_OUT_LABEL,
  SIGN_OUT_FAILED_LINE,
} from '../utils/phoneNumber';
import Footer from '../../src/components/Footer'

// Phone sign-in's invisible reCAPTCHA lives in its own container, never the
// PhoneVerificationScreen one ('recaptcha-container' and its global verifier).
const RECAPTCHA_CONTAINER_ID = 'recaptcha-container-login';
// The guard never waits on the Google redirect check longer than this: a
// getRedirectResult that never settles must not keep a signed-in member on
// the sign-in forms (or on a spinner) inside the app.
const REDIRECT_GUARD_CAP_MS = 10000;

export default function LoginScreen({ navigation, route }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null); // 'discord' | 'twitch' | null

  // Phone sign-in (DESKTOP_APP_DESIGN_2026-10-20.md S2.4). Shown on the web
  // and in the desktop app alike; it never creates an account.
  const [phoneMode, setPhoneMode] = useState('closed'); // 'closed' | 'number' | 'code'
  const [phoneInput, setPhoneInput] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [phoneBusy, setPhoneBusy] = useState(null); // null | 'send' | 'confirm'
  const [sentTo, setSentTo] = useState('');
  const [sendLockedUntil, setSendLockedUntil] = useState(0);
  // One invisible verifier at a time, each rendered into a fresh child of the
  // container (utils/phoneNumber.js createRecaptchaSlot): lazy, held here.
  const recaptchaRef = useRef(null);
  if (!recaptchaRef.current) {
    recaptchaRef.current = createRecaptchaSlot({
      getDocument: () => (typeof document !== 'undefined' ? document : null),
      containerId: RECAPTCHA_CONTAINER_ID,
      makeVerifier: (host) => new RecaptchaVerifier(auth, host, { size: 'invisible' }),
    });
  }
  const confirmationRef = useRef(null);
  const throttleRef = useRef(null);
  if (!throttleRef.current) throttleRef.current = createPhoneThrottle();

  // Desktop guard (S2.2 item 8): inside the app a signed-in member sees
  // "Sign out first" instead of the sign-in forms. `leaving` is set when a
  // sign-in on this screen succeeded and the screen is navigating away, so
  // the guard never flashes in between.
  const [authUser, setAuthUser] = useState(auth.currentUser);
  const [authKnown, setAuthKnown] = useState(false);
  const [guardName, setGuardName] = useState(undefined); // undefined = loading
  const [guardBusy, setGuardBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [redirectSettled, setRedirectSettled] = useState(Platform.OS !== 'web');

  // S7.6 choice 3: /login?next=StreamingAccounts returns a fresh sign-in to
  // the Streaming Hub; any other value is ignored.
  const homeRoute = () => nextTarget(route?.params) || 'Home';
  const leaveTo = (name, params) => {
    setLeaving(true);
    navigation.replace(name, params);
  };

  // Check if we're returning from OAuth callback
  React.useEffect(() => {
    const handleOAuthCallback = async () => {
      const params = route?.params || {};
      const urlParams = Platform.OS === 'web' ? new URLSearchParams(window?.location?.search) : null;
      
      const code = params.code || urlParams?.get('code');
      const state = params.state || urlParams?.get('state');
      const provider = params.provider || urlParams?.get('provider') || detectProvider();

      if (code && provider) {
        setSocialLoading(provider);
        setError('');
        // The next this browser saved when the Discord/Twitch sign-in started
        // (the callback URL carries code and state only). Taken once.
        const savedNext = Platform.OS === 'web' ? takeOAuthNext(getSessionStorage()) : null;

        // R0b (2026-10-16): the callback must carry the one-time state this
        // browser saved when it started the sign-in. Without the check, a link
        // with someone else's code signed the visitor into that person's
        // account (login CSRF). One use only: cleared before the exchange.
        if (Platform.OS === 'web') {
          if (!takeOAuthState(state)) {
            const label = provider === 'twitch' ? 'Twitch' : 'Discord';
            setError(`We couldn't confirm this sign-in started in this browser. Please try signing in with ${label} again.`);
            if (window.history) window.history.replaceState({}, document.title, window.location.pathname);
            setSocialLoading(null);
            return;
          }
        }

        try {
          const functions = getFunctions();
          const callbackFn = provider === 'twitch' 
            ? httpsCallable(functions, 'handleTwitchCallback')
            : httpsCallable(functions, 'handleDiscordCallback');
          
          const result = await callbackFn({ code, state });

          if (result.data.success) {
            await signInWithCustomToken(auth, result.data.customToken);

            // Clear URL params
            if (Platform.OS === 'web' && window.history) {
              window.history.replaceState({}, document.title, window.location.pathname);
            }

            // New Discord/Twitch users (and any previously-abandoned pending
            // account) must finish onboarding — DOB (18+), a unique username,
            // and ToS acceptance — before the account is finalized and Crits
            // are granted. Route them into the signup wizard's completion mode.
            if (result.data.isNewUser || result.data.onboardingComplete === false) {
              leaveTo('SignUp', {
                oauthCompletion: true,
                provider,
                email: result.data.user?.email,
                displayName: result.data.user?.displayName,
              });
            } else {
              leaveTo(savedNext || homeRoute());
            }
          }
        } catch (e) {
          console.error(`${provider} auth error:`, e);
          setError(e.message || `${provider} login failed. Please try again.`);
          
          if (Platform.OS === 'web' && window.history) {
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } finally {
          setSocialLoading(null);
        }
      }
    };

    handleOAuthCallback();
  }, [route?.params]);

  // Detect which OAuth provider based on callback URL
  const detectProvider = () => {
    if (Platform.OS !== 'web') return null;
    const path = window?.location?.pathname || '';
    if (path.includes('twitch')) return 'twitch';
    if (path.includes('discord')) return 'discord';
    return null;
  };

  // A disabled account goes to RestoreAccount with no params: react-navigation
  // on the web writes them into the address bar and history, and
  // RestoreAccountScreen never read them. The email and phone legs share this.
  const goRestoreAccount = () => navigation.replace('RestoreAccount');

  const handleSignIn = async () => {
    setIsLoading(true);
    setError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
      leaveTo(homeRoute());
    } catch (e) {
      if (e.code === 'auth/user-disabled') {
        goRestoreAccount();
      } else {
        setError("Invalid email or password. Please try again.");
        console.error(e.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Shared post-auth routing for both the popup and redirect Google flows.
  // The users-doc read is capped so a stalled connection surfaces a retryable
  // error instead of an endless spinner.
  //
  // A Google account whose users doc is missing OR still pending
  // (onboardingComplete:false — written server-side by the onAuthUserCreated
  // trigger the moment the Auth account exists) has not finished onboarding:
  // route it into the wizard's completion mode, exactly like Discord/Twitch,
  // where completeOAuthSignup finalizes it server-side. Never the account step.
  const routeGoogleUser = async (user) => {
    const docSnap = await withTimeout(getDoc(doc(db, 'users', user.uid)), 10000, 'Sign-in');

    if (docSnap.exists() && docSnap.data()?.onboardingComplete !== false) {
      leaveTo(homeRoute());
    } else {
      leaveTo('SignUp', {
        oauthCompletion: true,
        provider: 'Google',
        email: user.email,
        displayName: user.displayName || '',
      });
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    try {
      const provider = new GoogleAuthProvider();

      // Mobile web: the popup flow can complete on Google's side yet never
      // deliver the result back to the page (in-app browsers sever the opener
      // channel), leaving the user buffering forever. Use the full-page
      // redirect instead; the mount effect below picks up the return leg.
      // The CritCast desktop app takes the redirect too (S2.6).
      if (preferRedirectForGoogle()) {
        setSocialLoading('google');
        await signInWithRedirect(auth, provider);
        return; // page navigates away
      }

      const result = await signInWithPopup(auth, provider);
      setSocialLoading('google');
      await routeGoogleUser(result.user);
      setSocialLoading(null);
    } catch (e) {
      setSocialLoading(null);
      handleGoogleError(e);
    }
  };

  // One catch for both Google legs (popup and redirect). Logs the code only:
  // an AuthError's customData can carry OAuth tokens, and the member sees
  // member copy, never the SDK message (S2.4). A closed popup shows nothing.
  const handleGoogleError = (e) => {
    const code = e && e.code;
    if (code === 'auth/user-disabled') {
      navigation.replace('RestoreAccount');
      return;
    }
    const line = googleSignInErrorText(e);
    if (!line) return;
    console.error('Google sign in error:', code);
    setError(line);
  };

  // Return leg of the mobile signInWithRedirect flow. On a normal visit
  // getRedirectResult resolves null immediately and this is a no-op.
  React.useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    const cap = setTimeout(() => setRedirectSettled(true), REDIRECT_GUARD_CAP_MS);
    (async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          setSocialLoading('google');
          await routeGoogleUser(result.user);
        }
      } catch (e) {
        handleGoogleError(e);
      } finally {
        clearTimeout(cap);
        setSocialLoading(null);
        setRedirectSettled(true);
      }
    })();
    return () => clearTimeout(cap);
  }, []);

  // Full-page redirect to the provider (Discord/Twitch aren't Firebase Auth
  // providers, so this is a custom OAuth handoff). The provider returns to
  // /auth/<provider>/callback, which this same screen handles above.
  const startSocialSignIn = async (provider, authUrlFn) => {
    setSocialLoading(provider);
    setError('');
    try {
      const result = await httpsCallable(getFunctions(), authUrlFn)();
      if (result.data?.authUrl) {
        if (Platform.OS === 'web') {
          sessionStorage.setItem('oauth_provider', provider);
          saveOAuthNext(getSessionStorage(), route?.params); // S7.6 choice 3 survives the provider round trip
          saveOAuthState(result.data.state);
          window.location.href = result.data.authUrl;
        } else {
          await Linking.openURL(result.data.authUrl);
        }
      }
    } catch (e) {
      console.error(`${provider} auth error:`, e);
      setError(`Failed to connect to ${provider}. Please try again.`);
      setSocialLoading(null);
    }
  };

  const handleDiscordSignIn = () => startSocialSignIn('discord', 'getDiscordAuthUrl');
  const handleTwitchSignIn = () => startSocialSignIn('twitch', 'getTwitchAuthUrl');

  // ---------------------------------------------------------------- desktop guard
  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setAuthUser(u);
      setAuthKnown(true);
    });
    return unsub;
  }, []);

  // The guard names the member by the public profile name (never the Auth
  // or users-doc displayName). Read only inside the desktop app.
  const authUid = authUser ? authUser.uid : null;
  React.useEffect(() => {
    if (!authUid || !isDesktopApp()) {
      setGuardName(undefined);
      return undefined;
    }
    let live = true;
    setGuardName(undefined);
    withTimeout(getDoc(doc(db, 'public_profiles', authUid)), 10000, 'Profile')
      .then((snap) => {
        if (live) setGuardName(snap.exists() ? (snap.data()?.userName || null) : null);
      })
      .catch(() => {
        if (live) setGuardName(null);
      });
    return () => { live = false; };
  }, [authUid]);

  const handleGuardSignOut = async () => {
    setGuardBusy(true);
    setError('');
    try {
      await signOutWithDesktopPush({ signOut, auth, isDesktop: isDesktopApp() });
    } catch (e) {
      console.error('Sign out failed:', e && e.code);
      setError(SIGN_OUT_FAILED_LINE);
    } finally {
      setGuardBusy(false);
    }
  };

  // ---------------------------------------------------------------- phone sign-in
  // The send lock re-enables the send buttons when it runs out.
  React.useEffect(() => {
    const wait = sendLockedUntil - Date.now();
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setSendLockedUntil(0), wait);
    return () => clearTimeout(t);
  }, [sendLockedUntil]);

  // The verifier is created on the first send and cleared on unmount.
  React.useEffect(() => () => {
    if (recaptchaRef.current) recaptchaRef.current.drop();
  }, []);

  // clear() the verifier and empty its container, so the next attempt
  // renders a fresh widget into a new host element.
  const dropVerifier = () => {
    if (recaptchaRef.current) recaptchaRef.current.drop();
  };

  const openPhone = () => {
    setError('');
    setCodeInput('');
    confirmationRef.current = null;
    setPhoneMode('number');
  };

  const closePhone = () => {
    setError('');
    setCodeInput('');
    confirmationRef.current = null;
    setPhoneMode('closed');
  };

  const pickDifferentNumber = () => {
    setError('');
    setCodeInput('');
    confirmationRef.current = null;
    setPhoneMode('number');
  };

  const handleSendCode = async () => {
    if (phoneBusy) return;
    setError('');
    const throttle = throttleRef.current;
    const state = throttle.sendState(Date.now());
    if (state === 'exhausted') {
      setError(PHONE_ERR.clientLocked);
      return;
    }
    if (state === 'wait') return; // the button is disabled; a stale press does nothing
    const phone = toE164(phoneInput);
    if (!phone) {
      setError(PHONE_INVALID_TEXT);
      return;
    }
    setPhoneBusy('send');
    try {
      // Lazy: the first press renders the widget. A verifier whose container
      // left the page (the screen showed its loading view in between) is
      // replaced by a fresh one in a new host element.
      const verifier = await recaptchaRef.current.get();
      const confirmation = await signInWithPhoneNumber(auth, phone, verifier);
      throttle.recordSend(Date.now());
      setSendLockedUntil(throttle.sendLockedUntil());
      confirmationRef.current = confirmation;
      setSentTo(phone);
      setCodeInput('');
      setPhoneMode('code');
    } catch (e) {
      dropVerifier(); // the next attempt renders a fresh widget
      console.error('Phone sign-in send error:', e && e.code);
      setError(phoneSendErrorText(e, { screen: 'login' }) || PHONE_ERR.unknownLogin);
    } finally {
      setPhoneBusy(null);
    }
  };

  const handleConfirmCode = async () => {
    if (phoneBusy) return;
    setError('');
    const throttle = throttleRef.current;
    if (throttle.codeLocked() || !confirmationRef.current) {
      confirmationRef.current = null;
      setPhoneMode('number');
      if (throttle.codeLocked()) setError(PHONE_ERR.clientLocked);
      return;
    }
    const code = codeInput.trim();
    if (!/^\d{6}$/.test(code)) {
      setError(PHONE_CODE_REQUIRED_TEXT);
      return;
    }
    setPhoneBusy('confirm');
    try {
      // Firebase signs in here, and CREATES an account when none uses this
      // number (there is no sign-in-only mode). A fresh record is deleted at
      // once (deleteUser also signs it out); the typed number stays.
      const result = await confirmationRef.current.confirm(code);
      if (afterPhoneConfirm(getAdditionalUserInfo(result)) === 'delete') {
        const outcome = await deleteOrSignOut({ user: result.user, auth, deleteUser, signOut });
        confirmationRef.current = null;
        setCodeInput('');
        setPhoneMode('number');
        setError(outcome.message);
      } else {
        // An existing account: Home, exactly like email sign-in; App.js then
        // applies the Terms and onboarding guards.
        leaveTo(homeRoute());
      }
    } catch (e) {
      const code2 = e && e.code;
      if (code2 === 'auth/user-disabled') {
        goRestoreAccount();
        return;
      }
      if (code2 === 'auth/invalid-verification-code') {
        const { locked } = throttle.recordWrongCode();
        if (locked) {
          confirmationRef.current = null;
          setCodeInput('');
          setPhoneMode('number');
          setError(PHONE_ERR.clientLocked);
          return;
        }
      }
      console.error('Phone sign-in confirm error:', code2);
      setError(phoneSendErrorText(e, { screen: 'login' }) || PHONE_ERR.unknownLogin);
    } finally {
      setPhoneBusy(null);
    }
  };

  // Show loading screen if processing OAuth callback
  if (socialLoading) {
    const providerName = socialLoading.charAt(0).toUpperCase() + socialLoading.slice(1);
    const bgColor =
      socialLoading === 'twitch' ? '#9146FF' :
      socialLoading === 'google' ? '#4285F4' : '#5865F2';
    
    return (
      <View style={[styles.container, styles.loadingContainer]}>
        <ActivityIndicator size="large" color={bgColor} />
        <Text style={styles.loadingText}>Signing in with {providerName}...</Text>
      </View>
    );
  }

  const isWeb = Platform.OS === 'web';
  const inApp = isDesktopApp();
  // S2.6: main loads /login?desktopAuth=blocked when a Google sign-in hops to
  // a page the app will not host.
  const blockedLine = desktopAuthLine(route?.params, isWeb && typeof window !== 'undefined' ? window.location?.search : '');
  // A sign-in in flight on this screen (or the redirect return leg still
  // pending) is not "already signed in": the guard waits for it to settle.
  const authBusy = isLoading || !!phoneBusy || leaving || !redirectSettled;
  const signedInNow = !!(authUser && auth.currentUser);
  const showGuard = inApp && signedInNow && !authBusy;
  // Inside the app a signed-in member waits on a spinner, never on the forms,
  // while the redirect check is still out (capped at REDIRECT_GUARD_CAP_MS).
  const guardPending = inApp && (
    !authKnown
    || (signedInNow && !redirectSettled && !leaving)
    || (showGuard && guardName === undefined)
  );
  const guardLine = showGuard
    ? loginGuard({ isDesktop: inApp, signedIn: true, currentUserName: guardName || null })
    : null;
  const sendLocked = sendLockedUntil > Date.now();
  const phoneOpen = isWeb && phoneMode !== 'closed';

  const renderPhoneBlock = () => {
    const busy = !!phoneBusy;
    if (phoneMode === 'code') {
      return (
        <View style={styles.phoneBlock}>
          <Text style={styles.phoneLead}>{codeSentLine(sentTo)}</Text>
          <TextInput
            style={styles.input}
            placeholder={PHONE_CODE_PLACEHOLDER}
            placeholderTextColor="#888"
            keyboardType="number-pad"
            maxLength={6}
            value={codeInput}
            onChangeText={setCodeInput}
            editable={!busy}
          />
          <TouchableOpacity
            style={[styles.button, styles.signin, busy && styles.disabled]}
            onPress={handleConfirmCode}
            disabled={busy}
            accessibilityRole="button"
          >
            {phoneBusy === 'confirm' ? <ActivityIndicator color="#000" /> : <Text style={styles.buttonText}>{PHONE_CONFIRM_LABEL}</Text>}
          </TouchableOpacity>
          <TouchableOpacity onPress={pickDifferentNumber} disabled={busy} accessibilityRole="link">
            <Text style={styles.phoneLink}>{USE_DIFFERENT_NUMBER_LABEL}</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={handleSendCode} disabled={busy || sendLocked} accessibilityRole="link">
            {phoneBusy === 'send'
              ? <ActivityIndicator color="#FCD000" style={styles.phoneLinkSpinner} />
              : <Text style={[styles.phoneLink, sendLocked && styles.phoneLinkDisabled]}>{TEXT_ME_A_NEW_CODE_LABEL}</Text>}
          </TouchableOpacity>
        </View>
      );
    }
    return (
      <View style={styles.phoneBlock}>
        <TextInput
          style={styles.input}
          placeholder={PHONE_NUMBER_PLACEHOLDER}
          placeholderTextColor="#888"
          keyboardType="phone-pad"
          autoComplete="tel"
          value={phoneInput}
          onChangeText={setPhoneInput}
          editable={!busy}
        />
        <Text style={styles.phoneHint}>{PHONE_REGION_HINT}</Text>
        <TouchableOpacity
          style={[styles.button, styles.signin, (busy || sendLocked) && styles.disabled]}
          onPress={handleSendCode}
          disabled={busy || sendLocked}
          accessibilityRole="button"
        >
          {phoneBusy === 'send' ? <ActivityIndicator color="#000" /> : <Text style={styles.buttonText}>{TEXT_ME_A_CODE_LABEL}</Text>}
        </TouchableOpacity>
        <Text style={styles.phoneNote}>{SMS_RATES_LINE}</Text>
        <TouchableOpacity onPress={closePhone} disabled={busy} accessibilityRole="link">
          <Text style={styles.phoneLink}>{BACK_TO_EMAIL_LABEL}</Text>
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <ScrollView contentContainerStyle={styles.container}>
      {/* Phone sign-in's invisible reCAPTCHA (web only; the widget is
          created on the first "Text me a code" press). */}
      {isWeb ? <View id={RECAPTCHA_CONTAINER_ID} /> : null}
      <Text style={styles.headline}>Login or Sign Up</Text>
      {blockedLine && !showGuard ? <Text style={styles.notice}>{blockedLine}</Text> : null}
      {error ? <Text style={styles.error}>{error}</Text> : null}

      {guardPending ? (
        <ActivityIndicator size="large" color="#FCD000" style={styles.guardSpinner} />
      ) : showGuard ? (
        // Inside the desktop app a second sign-in would silently replace the
        // member who is signed in, so every form is replaced (S2.2 item 8).
        <View style={styles.guardBox}>
          <Text style={styles.guardText}>{guardLine}</Text>
          <TouchableOpacity
            style={[styles.button, styles.signin]}
            onPress={handleGuardSignOut}
            disabled={guardBusy}
            accessibilityRole="button"
          >
            {guardBusy ? <ActivityIndicator color="#000" /> : <Text style={styles.buttonText}>{SIGN_OUT_LABEL}</Text>}
          </TouchableOpacity>
        </View>
      ) : (
        <>
          {phoneOpen ? renderPhoneBlock() : (
            <>
              <TextInput
                style={styles.input}
                placeholder="Email"
                placeholderTextColor="#888"
                autoCapitalize="none"
                keyboardType="email-address"
                value={email}
                onChangeText={setEmail}
              />
              <TextInput
                style={styles.input}
                placeholder="Password"
                placeholderTextColor="#888"
                secureTextEntry
                value={password}
                onChangeText={setPassword}
              />

              {/* Forgot password (FORGOT_PASSWORD_DESIGN.md 4.2): the typed address
                  goes over in memory, never as a route param (the address bar).
                  push, not navigate: a fresh Forgot page every time, never an older
                  one with an earlier address and result (review UX-2). The row only
                  places the link; the tap area is the link itself (review MOB-2). */}
              <View style={styles.forgotRow}>
                <TouchableOpacity
                  style={styles.forgotLink}
                  onPress={() => { setResetPrefill(email); navigation.push('ForgotPassword'); }}
                  accessibilityRole="link"
                >
                  <Text style={styles.forgotText}>{FORGOT_LINK_LABEL}</Text>
                </TouchableOpacity>
              </View>

              <TouchableOpacity style={[styles.button, styles.signin]} onPress={handleSignIn} disabled={isLoading}>
                {isLoading ? <ActivityIndicator color="#000" /> : <Text style={styles.buttonText}>Sign In</Text>}
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.button, styles.signup]}
                onPress={() => navigation.navigate('SignUp')}
              >
                <Text style={styles.buttonText}>Sign Up</Text>
              </TouchableOpacity>
            </>
          )}

          <Text style={styles.socialLabel}>Or</Text>
          <Text style={styles.socialLabel}>Sign in with</Text>

          <View style={styles.socialRow}>
            {/* Google */}
            <TouchableOpacity
              style={styles.socialButton}
              onPress={handleGoogleSignIn}
            >
              <Image
                source={require('../../assets/google.png')}
                style={styles.socialIcon}
                resizeMode="contain"
              />
            </TouchableOpacity>

            {/* Discord */}
            <TouchableOpacity
              style={[styles.socialButton, styles.discordButton]}
              onPress={handleDiscordSignIn}
            >
              <Image
                source={require('../../assets/discord.webp')}
                style={styles.socialIcon}
                resizeMode="contain"
              />
            </TouchableOpacity>

            {/* Twitch */}
            <TouchableOpacity
              style={[styles.socialButton, styles.twitchButton]}
              onPress={handleTwitchSignIn}
            >
              <Image
                source={require('../../assets/twitch.webp')}
                style={styles.socialIcon}
                resizeMode="contain"
              />
            </TouchableOpacity>
          </View>

          {/* Phone (S2.4): the third way under "Sign in with". It signs in
              existing accounts only; a number with no account gets the
              pinned message and nothing is created. */}
          {isWeb && !phoneOpen ? (
            <TouchableOpacity style={[styles.button, styles.phoneButton]} onPress={openPhone} accessibilityRole="button">
              <Text style={styles.phoneButtonText}>{PHONE_SIGN_IN_LABEL}</Text>
            </TouchableOpacity>
          ) : null}
        </>
      )}

      <TouchableOpacity onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}>
        <Text style={styles.link}>← Back</Text>
      </TouchableOpacity>

      <Footer navigation={navigation} />

      <StatusBar style="light" />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: '#000',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16
  },
  loadingContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#fff',
    marginTop: 16,
    fontSize: 16,
  },
  headline: {
    color: '#CCC',
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 24
  },
  input: {
    width: '80%',
    backgroundColor: '#222',
    color: '#fff',
    padding: 8,
    marginBottom: 12,
    borderRadius: 4
  },
  button: {
    width: '60%',
    padding: 12,
    alignItems: 'center',
    borderRadius: 6,
    marginVertical: 6
  },
  signin: {
    backgroundColor: '#FCD000'
  },
  signup: {
    backgroundColor: '#888'
  },
  buttonText: {
    color: '#000',
    fontWeight: 'bold'
  },
  link: {
    color: '#fff',
    marginTop: 16,
    textDecorationLine: 'underline'
  },
  forgotRow: {
    width: '80%',
    marginTop: -6,
    alignItems: 'flex-end'
  },
  forgotLink: {
    minHeight: 44,
    paddingHorizontal: 8,
    justifyContent: 'center'
  },
  forgotText: {
    color: '#FCD000',
    fontSize: 13,
    textDecorationLine: 'underline'
  },
  error: {
    color: 'salmon',
    marginBottom: 8
  },
  socialLabel: {
    color: '#888',
    fontSize: 13,
    textAlign: 'center',
    marginVertical: 4
  },
  socialRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 12
  },
  socialButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#232323',
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.13,
    shadowRadius: 3,
    elevation: 2
  },
  discordButton: {
    backgroundColor: '#5865F2',
  },
  twitchButton: {
    backgroundColor: '#9146FF',
  },
  socialIcon: {
    width: 28,
    height: 28
  },
  notice: {
    color: '#FCD000',
    width: '80%',
    textAlign: 'center',
    marginBottom: 12
  },
  guardSpinner: {
    marginVertical: 24
  },
  guardBox: {
    width: '80%',
    alignItems: 'center',
    marginVertical: 12
  },
  guardText: {
    color: '#fff',
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 12
  },
  disabled: {
    opacity: 0.5
  },
  phoneButton: {
    backgroundColor: '#232323',
    borderWidth: 1,
    borderColor: '#444'
  },
  phoneButtonText: {
    color: '#fff',
    fontWeight: 'bold'
  },
  phoneBlock: {
    width: '100%',
    alignItems: 'center'
  },
  phoneLead: {
    color: '#CCC',
    textAlign: 'center',
    marginBottom: 12
  },
  phoneHint: {
    width: '80%',
    color: '#888',
    fontSize: 13,
    textAlign: 'center',
    marginBottom: 6
  },
  phoneNote: {
    color: '#888',
    fontSize: 12,
    textAlign: 'center',
    marginTop: 2
  },
  phoneLink: {
    color: '#FCD000',
    fontSize: 14,
    marginTop: 12,
    textDecorationLine: 'underline',
    textAlign: 'center'
  },
  phoneLinkDisabled: {
    color: '#666'
  },
  phoneLinkSpinner: {
    marginTop: 12
  }
});